// PCM1: one 10 s capture chunk of a meeting take (BUILD 1 contract C-10).
// The client half of the codec. src/company-workspace/meetings/pcm1.mjs is the
// server half; test/workspace-meeting-capture-codec.mjs asserts that both
// produce identical bytes. Owner: WP-4.
//
// Chunk k of a take holds the frames with seq ∈ [100k, 100k+99] that the
// device has. It is sent when all 100 frames are spooled, or at take end with
// the FINAL flag. Its bytes are a pure function of those frames, so a retry
// after a lost response sends identical bytes and the server treats it as a
// no-op.
import type { PcmFrame } from '@/speech/contracts';

export const PCM1_HEADER_BYTES = 24;
export const PCM1_FRAME_HEADER_BYTES = 8;
export const PCM1_FLAG_FINAL = 1;
export const CAPTURE_SAMPLE_RATE = 16000;
export const CAPTURE_FRAME_SAMPLES = 1600;
export const CAPTURE_FRAMES_PER_CHUNK = 100;

export type ChunkFrame = Pick<PcmFrame, 'seq' | 'pcm'>;
export type EncodedChunk = { take: number; chunkIndex: number; final: boolean; bytes: Uint8Array };

/** The chunk a frame belongs to. */
export const chunkOfSeq = (seq: number) => Math.floor(seq / CAPTURE_FRAMES_PER_CHUNK);
/** The seq range [first, last] chunk `chunkIndex` covers. */
export const chunkSeqRange = (chunkIndex: number): [number, number] => [
  chunkIndex * CAPTURE_FRAMES_PER_CHUNK,
  chunkIndex * CAPTURE_FRAMES_PER_CHUNK + CAPTURE_FRAMES_PER_CHUNK - 1,
];

/**
 * Encode one chunk. `frames` must be sorted by seq and lie in the chunk's
 * range; the caller (the recorder's uploader) builds them from the spool.
 */
export function encodePcm1(input: {
  take: number;
  chunkIndex: number;
  final: boolean;
  frames: ChunkFrame[];
}): EncodedChunk {
  const size = input.frames.reduce(
    (total, frame) => total + PCM1_FRAME_HEADER_BYTES + frame.pcm.byteLength,
    PCM1_HEADER_BYTES,
  );
  const bytes = new Uint8Array(size);
  const view = new DataView(bytes.buffer);
  bytes.set([0x50, 0x43, 0x4d, 0x31], 0); // "PCM1"
  view.setUint8(4, 1);
  view.setUint8(5, 1);
  view.setUint32(8, CAPTURE_SAMPLE_RATE, true);
  view.setUint32(12, input.take, true);
  view.setUint32(16, input.chunkIndex, true);
  view.setUint16(20, input.frames.length, true);
  view.setUint8(22, input.final ? PCM1_FLAG_FINAL : 0);
  let offset = PCM1_HEADER_BYTES;
  for (const frame of input.frames) {
    view.setUint32(offset, frame.seq, true);
    view.setUint16(offset + 4, frame.pcm.byteLength / 2, true);
    bytes.set(new Uint8Array(frame.pcm), offset + PCM1_FRAME_HEADER_BYTES);
    offset += PCM1_FRAME_HEADER_BYTES + frame.pcm.byteLength;
  }
  return { take: input.take, chunkIndex: input.chunkIndex, final: input.final, bytes };
}

/** Lowercase hex sha256 of the chunk, sent as `x-poneva-content-sha256`. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Samples a chunk carries (for the "Σ captured = Σ uploaded" check). */
export const chunkSamples = (frames: ChunkFrame[]) =>
  frames.reduce((total, frame) => total + frame.pcm.byteLength / 2, 0);
